import demoLibraryFace1Webp from '@assets/welcome-library/optimized/welcome-library-faces/01.webp';
import demoLibraryFace2Webp from '@assets/welcome-library/optimized/welcome-library-faces/02.webp';
import demoLibraryFace3Webp from '@assets/welcome-library/optimized/welcome-library-faces/03.webp';
import demoLibraryFace4Webp from '@assets/welcome-library/optimized/welcome-library-faces/04.webp';
import demoLibraryFace5Webp from '@assets/welcome-library/optimized/welcome-library-faces/05.webp';
import demoLibraryFace6Webp from '@assets/welcome-library/optimized/welcome-library-faces/06.webp';
import demoLibraryFace7Webp from '@assets/welcome-library/optimized/welcome-library-faces/07.webp';
import welcomeLibraryVideo720pAv1 from '@assets/welcome-library/optimized/welcome-library-video/welcome-library-video_720p_av1.mp4';
import welcomeLibraryVideo720pH264 from '@assets/welcome-library/optimized/welcome-library-video/welcome-library-video_720p_h264.mp4';
import WelcomeLibraryThumbnail from '@assets/welcome-library/optimized/welcome-library-video/welcome-library-video_poster.jpg';
import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { GradientOverlay } from '@components/GradientOverlay/GradientOverlay';
import { LibraryCardPlaceholder } from '@components/LibraryThumbnail/LibraryCardPlaceholder';
import { useLibraryCover } from '@components/LibraryThumbnail/useLibraryThumbnail';
import { VerbRail } from '@components/VerbRail/VerbRail';
import { useCenteredCard } from '@lib/hooks/CenteredCardContext';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { useLibraryVerbs } from '@lib/hooks/useLibraryVerbs';
import { trackCardLight } from '@lib/style/cardLight';
import { joinLibraryStats } from '@lib/utils/libraryStats';
import { formatNumber, pluralize } from '@lib/utils/units';
import { getLibraryUrl } from '@lib/utils/url';
import FolderRounded from '@mui/icons-material/FolderRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PlayCircleRounded from '@mui/icons-material/PlayCircleRounded';
import { LibraryStore } from '@stores/Library/Library';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { RefObject, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import css from './LibraryCard.module.scss';
import { LibraryDeleteModalRef } from './LibraryDeleteModal';
import { LibraryMenuButton } from './LibraryMenuButton';
import { buildLibraryRailItems } from './libraryRailItems';
import { LibraryRenameModalRef } from './LibraryRenameModal';
import { TranscriptOnlyIndicator } from './TranscriptOnlyIndicator';

/**
 * Dumb presentational card. All data is provided via props — no store
 * access, no thumbnail fetching, no `latestMediaIds` cycling. The smart
 * wrapper(s) below own that logic.
 *
 * Thumbnail render modes (in priority order):
 *   - `isWelcomeLibrary`: renders the demo library video / poster.
 *   - `thumbnail` (direct URL, e.g. blob: or static asset): renders <img>.
 *   - `thumbnailUrl` (backend URL needing auth): renders <AuthenticatedImage>.
 *   - none of the above: renders <LibraryCardPlaceholder seed={placeholderSeed} />.
 *
 * On image error, calls `onThumbnailError` so the wrapper can advance its
 * fallback chain or fall back to the placeholder by clearing thumbnail props.
 */
export interface LibraryCardViewProps extends React.HTMLAttributes<HTMLAnchorElement> {
  id: string;
  name: string;
  /** Undefined while the stats load; the counts line waits for it. See `formatLibraryCardMeta`. */
  mediaCount?: number;
  /** Folders at the top level, shown beside the media count when there are any. */
  folderCount?: number;
  /**
   * The owner, on its own line under the counts, and only when that owner isn't you — see
   * `formatLibraryCardMeta`. A separate line rather than beside the counts so the card can
   * render it a size down and dimmer; a plain string rather than a node so `LibraryCard`'s memo
   * still holds.
   */
  ownerLine?: string;
  href: string;
  thumbnail?: string;
  thumbnailUrl?: string;
  thumbnailToken?: string;
  onThumbnailError?: () => void;
  placeholderSeed: string | number;
  isWelcomeLibrary?: boolean;
  showWelcomeLibraryBadge?: boolean;
  noLink?: boolean;
  /** When the href is absolute (http/https), the card defaults to opening
   * in a new tab. Set this to true to keep the navigation in-window. */
  sameWindow?: boolean;
  isHidden?: boolean;
  hideOptions?: boolean;
  isReadOnly?: boolean;
  /** When true, shows the upper-left indicator that this library only supports transcript search. */
  isTranscriptOnly?: boolean;
  disableDelete?: boolean;
  onSelect?: () => void;
  onMobileMenuClick?: (thumbnailBlobUrl?: string) => void;
  renameModalRef?: RefObject<LibraryRenameModalRef | null>;
  deleteModalRef?: RefObject<LibraryDeleteModalRef | null>;
  /** Verb rail actions. Both must be supplied for the rail to render. */
  onWorkWithAgent?: () => void;
  onFindMoments?: () => void;
  /**
   * Replaces the verb rail with a single "Add media" action. Set for a library with
   * no media, where both verbs would open something with nothing in it.
   */
  isEmpty?: boolean;
  onAddMedia?: () => void;
  /**
   * Ref for the card's root element. Owned by `LibraryCard`, which needs a DOM node
   * to observe for its deferred cover but renders none of its own; the view reads
   * the same node for centre-of-viewport tracking and for grabbing the rendered
   * thumbnail on a ⋯ tap. Required rather than optional so that stays one ref on
   * one element instead of two that can disagree.
   */
  cardRef: RefObject<HTMLDivElement | null>;
}

/**
 * The counts under the name, as glyphs: media, then folders when there are any. Glyphs rather
 * than "24 media · 3 folders" so the name stays the line you read, and so the line has room
 * left. The words are kept for screen readers and the tooltip.
 *
 * Media is a play mark, not a stack of frames: the stack is the sidebar's Artifacts icon, and the
 * Libraries icon is a stack too, so either would name the wrong thing.
 */
const LibraryCardCounts = ({
  mediaCount,
  folderCount,
}: {
  mediaCount: number;
  folderCount: number;
}) => {
  const label = joinLibraryStats([
    pluralize(mediaCount, 'media'),
    folderCount > 0 ? pluralize(folderCount, 'folder') : null,
  ]);
  return (
    <span className={css.counts} title={label}>
      <span className={css.count} aria-hidden>
        <PlayCircleRounded className={css.countIcon} />
        {formatNumber(mediaCount)}
      </span>
      {folderCount > 0 && (
        <span className={css.count} aria-hidden>
          <FolderRounded className={css.countIcon} />
          {formatNumber(folderCount)}
        </span>
      )}
      <span className={css.visuallyHidden}>{label}</span>
    </span>
  );
};

export const LibraryCardView = ({
  id,
  name,
  mediaCount,
  folderCount = 0,
  ownerLine,
  href,
  thumbnail,
  thumbnailUrl,
  thumbnailToken,
  onThumbnailError,
  placeholderSeed,
  isWelcomeLibrary = false,
  showWelcomeLibraryBadge = false,
  noLink = false,
  sameWindow = false,
  isHidden = false,
  hideOptions,
  isReadOnly = false,
  isTranscriptOnly = false,
  disableDelete = false,
  onSelect,
  onMobileMenuClick,
  renameModalRef,
  deleteModalRef,
  onWorkWithAgent,
  onFindMoments,
  isEmpty = false,
  onAddMedia,
  cardRef,
  ...divProps
}: LibraryCardViewProps) => {
  const isMobile = useIsMobile();
  const { centeredId, register } = useCenteredCard();
  const isCentered = isMobile && centeredId === id;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isWelcomeLibrary && videoRef.current) {
      videoRef.current.playbackRate = 1.5;
    }
  }, [isWelcomeLibrary]);

  // Register this card for center-of-viewport tracking on mobile
  useEffect(() => {
    if (!isMobile || !cardRef.current) return;
    return register(id, cardRef.current);
  }, [isMobile, id, register]);

  const handleMobileMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Grab the blob URL from the already-rendered thumbnail — avoids async flicker
    const img = cardRef.current?.querySelector('img');
    onMobileMenuClick?.(img?.src || undefined);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.();
  };

  const handleMouseEnter = () => {
    if (isWelcomeLibrary && videoRef.current) {
      videoRef.current.playbackRate = 1.5;
      videoRef.current.play().catch(console.error);
    }
  };

  const handleMouseLeave = () => {
    if (isWelcomeLibrary && videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Absolute URLs (http/https) render as a target="_blank" anchor; relative
  // paths use react-router's <Link>. Detected from the href so callers don't
  // need to pass an extra `external` flag.
  const isExternal = !noLink && /^https?:\/\//.test(href);

  // Shared with the desktop table's Name cell so both agree on order, labels and
  // which glyph is accented.
  const railItems =
    onWorkWithAgent && onFindMoments
      ? buildLibraryRailItems({
          isEmpty,
          isReadOnly,
          onWorkWithAgent,
          onAddMedia,
          onFindMoments,
        })
      : [];

  const cardClassName = classNames(css.libraryCard, {
    [css.mobile]: isMobile,
    [css.centered]: isCentered,
    [css.hidden]: isHidden,
  });

  const innerContent = (
    <>
      {isWelcomeLibrary ? (
        <video
          ref={videoRef}
          poster={WelcomeLibraryThumbnail}
          muted
          playsInline
          className={css.thumbnailImg}
        >
          <source src={welcomeLibraryVideo720pAv1} type="video/mp4; codecs=av01.0.04M.08" />
          <source src={welcomeLibraryVideo720pH264} type="video/mp4; codecs=avc1.4D401E" />
        </video>
      ) : thumbnail ? (
        <img src={thumbnail} alt={name} className={css.thumbnailImg} />
      ) : thumbnailUrl ? (
        <AuthenticatedImage
          src={thumbnailUrl}
          token={thumbnailToken}
          alt={name}
          className={css.thumbnailImg}
          onError={onThumbnailError}
        />
      ) : (
        <LibraryCardPlaceholder seed={placeholderSeed} />
      )}
      <GradientOverlay borderRadius={24} />

      {isTranscriptOnly && <TranscriptOnlyIndicator className={css.transcriptBadge} />}

      <div className={css.upperRight}>
        {!hideOptions &&
          !isReadOnly &&
          (isMobile ? (
            <button className={css.mobileMenuButton} onClick={handleMobileMenuClick}>
              <MoreHorizIcon fontSize="small" />
            </button>
          ) : (
            renameModalRef &&
            deleteModalRef && (
              <LibraryMenuButton
                onClick={handleMenuClick}
                disableDelete={disableDelete}
                renameModalRef={renameModalRef}
                deleteModalRef={deleteModalRef}
                libraryId={id}
              />
            )
          ))}
      </div>
      {isWelcomeLibrary && showWelcomeLibraryBadge && (
        <div className={css.welcomeLibraryBadge}>
          <span>Get started with the demo library</span>
        </div>
      )}

      <div className={css.libraryCardInfo}>
        <div className={css.libraryCardInfoContent}>
          <GradientOverlay variant="bottom-blur" backdropBlur={60} height={140} zIndex={1} />
          {/* Bottom-aligned just above the title, out of flow so it never resizes
              the info block or shifts the name on hover.
              Deliberately not beside the kebab — that cluster holds Rename and
              Delete, and adjacency would demote these verbs to housekeeping. And
              deliberately not card-centre, which is both the card's own open
              target and where the demo video starts playing on the very hover
              that reveals this.
              Gated on `hideOptions`, NOT on `isReadOnly`: both verbs are read
              actions, and the read-only demo library is the highest-value place
              for them — today it has no agent entry point at all. */}
          {/* Rendered on mobile too. There, "hovered" means the card is crossing the
              viewport's centre line — `useCenteredCard` already drives the same
              treatment for this card's border and the demo-faces scale, so the rail
              reveals off `.centered` rather than `:hover`. */}
          {!hideOptions && railItems.length > 0 && (
            <VerbRail className={css.verbRail} items={railItems} />
          )}
          {isWelcomeLibrary && (
            <div className={css.demoLibraryFaces}>
              <picture>
                <source srcSet={demoLibraryFace1Webp} type="image/webp" />
                <img src={demoLibraryFace1Webp} alt="Demo Library Face 1" data-index="1" />
              </picture>
              <picture>
                <source srcSet={demoLibraryFace2Webp} type="image/webp" />
                <img src={demoLibraryFace2Webp} alt="Demo Library Face 2" data-index="2" />
              </picture>
              <picture>
                <source srcSet={demoLibraryFace3Webp} type="image/webp" />
                <img src={demoLibraryFace3Webp} alt="Demo Library Face 3" data-index="3" />
              </picture>
              <picture>
                <source srcSet={demoLibraryFace4Webp} type="image/webp" />
                <img src={demoLibraryFace4Webp} alt="Demo Library Face 4" data-index="4" />
              </picture>
              <picture>
                <source srcSet={demoLibraryFace5Webp} type="image/webp" />
                <img src={demoLibraryFace5Webp} alt="Demo Library Face 5" data-index="5" />
              </picture>
              <picture>
                <source srcSet={demoLibraryFace6Webp} type="image/webp" />
                <img src={demoLibraryFace6Webp} alt="Demo Library Face 6" data-index="6" />
              </picture>
              <picture>
                <source srcSet={demoLibraryFace7Webp} type="image/webp" />
                <img src={demoLibraryFace7Webp} alt="Demo Library Face 7" data-index="7" />
              </picture>
            </div>
          )}
          <h3 title={name}>{name}</h3>
          {mediaCount !== undefined && (
            <LibraryCardCounts mediaCount={mediaCount} folderCount={folderCount} />
          )}
          {/* Always takes its line, empty or not, so every card in a row puts its name and counts
              at the same height — otherwise the cards somebody else owns sit a line higher. */}
          <span className={css.ownerLine} aria-hidden={!ownerLine}>
            {ownerLine ?? '\u00a0'}
          </span>
        </div>
      </div>
    </>
  );

  if (isExternal) {
    return (
      <a
        ref={cardRef as any}
        href={href}
        {...(sameWindow ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
        className={cardClassName}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onPointerMove={trackCardLight}
        {...divProps}
      >
        {innerContent}
      </a>
    );
  }

  return (
    <Link
      ref={cardRef as any}
      to={noLink ? '#' : href}
      className={cardClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerMove={trackCardLight}
      {...divProps}
    >
      {innerContent}
    </Link>
  );
};

/**
 * Smart wrapper that adapts a LibraryStore to LibraryCardView. Owns the
 * thumbnail-fallback state machine (cycles through `library.latestMediaIds`
 * up to 5 times when the current attempt errors) and computes the
 * library-flavoured fields (href via getLibraryUrl, disableDelete from
 * library flags). Wrapped in `observer()` so MobX tracks the library
 * field reads done inside.
 */
export interface LibraryCardProps extends React.HTMLAttributes<HTMLAnchorElement> {
  id: string;
  name: string;
  /** Forwarded to the view — see `LibraryCardViewProps.mediaCount`. */
  mediaCount?: number;
  folderCount?: number;
  /** Forwarded to the view — see `LibraryCardViewProps.ownerLine`. */
  ownerLine?: string;
  library: LibraryStore;
  hideOptions?: boolean;
  onSelect?: () => void;
  thumbnail?: string;
  thumbnailMediaId?: string;
  isWelcomeLibrary?: boolean;
  showWelcomeLibraryBadge?: boolean;
  noLink?: boolean;
  hoveredMediaId?: string | null;
  isHidden?: boolean;
  renameModalRef?: RefObject<LibraryRenameModalRef | null>;
  deleteModalRef?: RefObject<LibraryDeleteModalRef | null>;
  onMobileMenuClick?: (thumbnailBlobUrl?: string) => void;
  /**
   * Opens the add-media modal for this library. Only reached when the library has
   * no media — see the rail logic in `LibraryCardView`. The modal itself is owned
   * one level up (`LibrariesPage`) so the grid mounts one, not one per card.
   */
  onAddMedia?: () => void;
}

export const LibraryCard = observer(function LibraryCard({
  id,
  name,
  mediaCount,
  folderCount,
  ownerLine,
  library,
  hideOptions,
  onSelect,
  thumbnail: propThumbnail,
  thumbnailMediaId,
  isWelcomeLibrary = false,
  showWelcomeLibraryBadge = false,
  noLink = false,
  hoveredMediaId,
  isHidden = false,
  renameModalRef,
  deleteModalRef,
  onMobileMenuClick,
  onAddMedia,
  ...divProps
}: LibraryCardProps) {
  // The ref is owned here rather than inside `LibraryCardView` because this component
  // renders no DOM node of its own and the cover deferral needs one to observe; the view
  // takes it as a prop and uses it for its own centre-tracking too, so there is still
  // exactly one ref on the card's root.
  //
  // The welcome library renders a bundled poster, so a fetched cover would be discarded —
  // that reason is OR'd with the hook's own out-of-window one. Cover resolution (including
  // the latestMediaIds fallback chain) is shared with the Libraries list rows and the
  // search-bar library switcher.
  const cardRef = useRef<HTMLDivElement>(null);
  const {
    url: fetchedUrl,
    token: fetchedToken,
    onError: handleThumbnailError,
  } = useLibraryCover(library, cardRef, {
    layout: 'grid',
    thumbnailMediaId,
    hoveredMediaId,
    disabled: isWelcomeLibrary,
  });

  const { workWithAgent, findMoments } = useLibraryVerbs();
  const railOrigin = { surface: 'libraries' as const, control: 'library-card-rail' };

  return (
    <LibraryCardView
      {...divProps}
      cardRef={cardRef}
      id={id}
      name={name}
      mediaCount={mediaCount}
      folderCount={folderCount}
      ownerLine={ownerLine}
      href={getLibraryUrl(id, name)}
      thumbnail={propThumbnail}
      thumbnailUrl={propThumbnail ? undefined : fetchedUrl}
      thumbnailToken={propThumbnail ? undefined : fetchedToken}
      onThumbnailError={handleThumbnailError}
      placeholderSeed={id}
      isWelcomeLibrary={isWelcomeLibrary}
      showWelcomeLibraryBadge={showWelcomeLibraryBadge}
      noLink={noLink}
      isHidden={isHidden}
      hideOptions={hideOptions}
      isReadOnly={library.isReadOnly}
      isTranscriptOnly={library.isTranscriptOnly}
      disableDelete={library.isDefault || library.isDemo || library.isReadOnly}
      onSelect={onSelect}
      onMobileMenuClick={onMobileMenuClick}
      renameModalRef={renameModalRef}
      deleteModalRef={deleteModalRef}
      onWorkWithAgent={() => workWithAgent(id, railOrigin)}
      onFindMoments={() => findMoments(id, railOrigin)}
      // Only `=== 0` counts as empty. Undefined stats (still loading) fall through
      // to the verbs, matching how the in-library action row fails open rather than
      // flashing the wrong action while the count arrives.
      isEmpty={library.stats?.count === 0}
      onAddMedia={onAddMedia}
    />
  );
});
